﻿<template>
    <div class="cart-summary w-full md:w-5/12 lg:w-4/12">
        <div class="basket-summary border border-form rounded w-full px-8 md:p-4 lg:p-4 flex flex-col">
            <itinerary_breakdown v-if="show_breakdown" display_open=true></itinerary_breakdown>
            <hr v-if="show_breakdown"/>
            <trip_summary v-if="show_summary"></trip_summary>

            <!-- Status Header & Message -->
            <div class="mb-6">
                <h2 class="text-heading font-bold text-2xl mb-2" v-if="isCheckingPaymentStatus">
                    <p class="inline-block ml-1 mt-0 mr-2 text-grey-default">Checking payment status</p>
                    <svg class="animate-spin h-5 w-5 mr-3 inline text-grey-default" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 512 512" stroke="currentColor">
                        <path fill="currentColor" d="M440.65 12.57l4 82.77A247.16 247.16 0 0 0 255.83 8C134.73 8 33.91 94.92 12.29 209.82A12 12 0 0 0 24.09 224h49.05a12 12 0 0 0 11.67-9.26 175.91 175.91 0 0 1 317-56.94l-101.46-4.86a12 12 0 0 0-12.57 12v47.41a12 12 0 0 0 12 12H500a12 12 0 0 0 12-12V12a12 12 0 0 0-12-12h-47.37a12 12 0 0 0-11.98 12.57zM255.83 432a175.61 175.61 0 0 1-146-77.8l101.8 4.87a12 12 0 0 0 12.57-12v-47.4a12 12 0 0 0-12-12H12a12 12 0 0 0-12 12V500a12 12 0 0 0 12 12h47.35a12 12 0 0 0 12-12.6l-4.15-82.57A247.17 247.17 0 0 0 255.83 504c121.11 0 221.93-86.92 243.55-201.82a12 12 0 0 0-11.8-14.18h-49.05a12 12 0 0 0-11.67 9.26A175.86 175.86 0 0 1 255.83 432z" class=""></path>
                    </svg>
                </h2>
                <h2 class="text-heading font-bold text-2xl mb-2" v-else>
                    {{ isAgent_Login() ? 'Payment status' : !is_last_payment_success() ? 'Payment unsuccessful' : is_part_paid() ? 'Deposit received' : is_fully_paid() ? 'Fully paid' : 'Checking payment status' }}
                </h2>
                <p class="text-grey-dark text-sm leading-relaxed mb-4">
                    <template v-if="is_part_paid() && is_last_payment_success()">
                        A partial payment was received but your booking is not yet fully paid. Your booking reference is <strong>#{{ $store.getters.get_Booking?.group_booking_id }}</strong>. 
                        To pay your remaining balance, request another payment link or reach out if you have questions:
                    </template>
                    <template v-else-if="is_fully_paid()">
                        Thank you for your payment. Your booking reference is <strong>#{{ $store.getters.get_Booking?.group_booking_id }}</strong> - please quote this if you need to contact us:
                    </template>
                    <template v-else>
                        Your payment was unsuccessful or cancelled. You have not been charged. Your booking reference is <strong>#{{ $store.getters.get_Booking?.group_booking_id }}</strong> - please quote this if you contact us. 
                        To complete your booking, please try again or get in touch:
                    </template>
                </p>

                <!-- Brand Contact Block -->
                <div class="w-full text-left text-sm mb-6">
                    <a v-if="GetBrand() == 'HEX'" href="mailto:info@highlandexplorertours.com" class="text-action font-semibold hover:underline">info@highlandexplorertours.com</a>
                    <a v-else-if="GetBrand() == 'HAG'" href="mailto:info@haggisadventures.com" class="text-action font-semibold hover:underline">info@haggisadventures.com</a>
                    <a v-else-if="GetBrand() == 'AAT' || GetBrand() == 'aatkings'" href="https://www.aatkings.com/contact-us" class="text-action font-semibold hover:underline" target="_blank" rel="noopener noreferrer">https://www.aatkings.com/contact-us</a>
                    <a v-else-if="GetBrand() == 'SEIT'" href="https://seittours.com/contact-us/" class="text-action font-semibold hover:underline" target="_blank" rel="noopener noreferrer">https://seittours.com/contact-us/</a>
                </div>
            </div>

            <!-- Outstanding Balance Details Box -->
            <div v-if="has_balance_outstanding()" class="bg-gray-50 border border-gray-100 rounded-2xl p-6 my-6 text-left">
                <h6 class="text-lg text-heading font-bold mb-4">
                    {{ is_part_paid() ? 'Remaining Balance Details' : 'Payment Summary' }}
                </h6>

                <!-- Financial Breakdown -->
                <div class="space-y-3 text-sm text-grey-dark">
                    <!-- Total outstanding (Global booking balance) -->
                    <div class="flex justify-between items-center pb-2 border-b border-gray-200/60">
                        <span>Total Outstanding</span>
                        <span class="font-bold text-black">{{ get_total_balance_outstanding_formatted() }}</span>
                    </div>

                    <!-- Due Date Info -->
                    <div class="flex justify-between items-center text-xs pb-2 border-b border-gray-200/60">
                        <span>Payment Due Date</span>
                        <span class="font-medium text-black">{{ Balance_Due_Date() }}</span>
                    </div>

                    <!-- Current Charge / Payable Amount details -->
                    <div class="pt-2">
                        <div class="flex justify-between items-baseline">
                            <span class="font-semibold text-black text-base">Amount to Pay</span>
                            <div class="text-right">
                                <span class="text-2xl font-black text-action">
                                    {{ get_payment_balance_formatted() }} <span class="text-sm font-normal text-grey-dark">{{ get_Currency() }}</span>
                                </span>
                            </div>
                        </div>
                        
                        <!-- Fee Integrated Inline and Subtly -->
                        <div v-if="get_payment_fees() > 0" class="flex justify-between items-center text-xs text-grey-dark mt-1">
                            <span>Includes card processing & local taxes</span>
                            <span>(Fee: {{ get_payment_fees_formatted() }} {{ get_Currency() }})</span>
                        </div>
                    </div>
                </div>

                <!-- Actions -->
                <div class="mt-6">
                    <button v-if="is_not_paid() || !is_last_payment_success()" type="button" class="button button-pink-book w-full py-3 text-center print:hidden font-semibold" v-on:click="redirect_to_paymentpage(get_payment_amount())">
                        Pay now
                    </button>
                </div>
            </div>

            <div class="bg-white border border-gray-100 rounded-2xl p-6 my-6 text-left">
                <div class="border-b border-gray-100 pb-3 mb-4">
                    <h6 class="text-base text-heading font-bold">
                        Payments Received
                    </h6>
                    <span class="text-xs text-grey-dark">Includes all card processing fees and taxes</span>
                </div>

                <div v-if="!has_payments()" class="py-1 text-center">
                    <p class="text-sm text-grey-dark italic">No payments received yet.</p>
                </div>   

                <div v-else class="space-y-1">
                    <div v-for="(payment, index) in get_payments()" :key="index" class="flex justify-between items-center py-1">
                        <span class="text-sm text-gray-500 font-medium">
                            {{ payment.date_made }}
                        </span>
                        <span class="text-sm font-semibold text-black">
                            {{ payment.amount_formatted }}
                            <span class="text-xs font-normal text-gray-400 ml-1">
                                {{ payment.iso_currency_code }}
                            </span>
                        </span>
                    </div>

                    <div class="mt-4 pt-4 border-t border-gray-200/60">
                        <div class="flex justify-between items-center">
                            <span class="text-sm font-semibold text-black">
                                Total Amount Paid
                            </span>
                            <span class="text-xl font-black text-black">
                                {{ get_total_price_paid_formatted() }}
                                <span class="text-xs font-normal text-gray-500 ml-1">
                                    {{ $store.getters.get_Currency.currency_info.iso_currency_code }}
                                </span>
                            </span>
                        </div>
                    </div>
                </div>

                <div v-if="!is_fully_paid()" class="mt-5 p-3 rounded-md bg-gray-50 border border-gray-100 flex items-center justify-between text-xs text-gray-500" style="border-color: lightgray;">
                    <div class="flex items-center space-x-2">
                        <svg class="w-4 h-4 text-gray-400 shrink-0 text-action" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        <span>It can take a few minutes for payments to be processed.</span>
                    </div>

                    <button 
                        type="button"
                        @click="reloadPage" 
                        class="inline-flex items-center space-x-1.5 font-semibold text-blue-600 hover:text-blue-700 transition-colors cursor-pointer shrink-0 ml-2 focus:outline-none text-action"
                    >
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg>
                        <span>Click here to refresh</span>
                    </button>
                </div>
            </div>

        </div>
    </div>
</template>
<script lang="ts" src="./summary.ts"></script>
